import {
  BackEndValueType,
  EDatasourceType,
  ESdChangeReason,
  IActionDefinitionDto,
  IActionParameter,
  IBlocklyActionInfo,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import Blockly from "blockly";
import { javascriptGenerator } from "blockly/javascript";
import _ from "lodash";
import ObjectHash from "object-hash";
import { KeyboardEvent, ReactNode, createRef } from "react";
import { BlocklyWorkspace } from "react-blockly";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import "../../../blockly/index";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEditorHeader from "../../ui-kit/kuika-editor-header/kuika-editor-header";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { ACTION_NAME_MESSAGE, ACTION_NAME_PATTERN } from "./datasource-create-actions";
import { requestDatasourceReveal } from "./datasource-reveal";
import { BlocklyActionSwitcherChip } from "./editor-switcher/blockly-action-switcher-chip";
import "./editor-bar-surface.scss";

const initialXml =
  '<xml xmlns="https://developers.google.com/blockly/xml"><block type="kfunction_main" id="=PkjgIy3Ra2?[C`h!0hV" deletable="false" x="210" y="70"></block></xml>';
const toolbox = {
  kind: "categoryToolbox",
  contents: [
    {
      kind: "category",
      name: "Logic",
      colour: "#5b80a5",
      contents: [
        {
          kind: "block",
          type: "controls_if"
        },
        {
          kind: "block",
          type: "logic_compare",
          fields: {
            OP: "EQ"
          }
        },
        {
          kind: "block",
          type: "logic_operation",
          fields: {
            OP: "AND"
          }
        },
        {
          kind: "block",
          type: "logic_negate"
        },
        {
          kind: "block",
          type: "logic_boolean",
          fields: {
            BOOL: "TRUE"
          }
        },
        {
          kind: "block",
          type: "logic_null"
        },
        {
          kind: "block",
          type: "logic_ternary"
        }
      ]
    },
    {
      kind: "category",
      name: "Loops",
      colour: "#5ba55b",
      contents: [
        {
          kind: "block",
          type: "controls_repeat_ext",
          values: {
            TIMES: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "10"
              }
            }
          }
        },
        {
          kind: "block",
          type: "controls_whileUntil",
          fields: {
            MODE: "WHILE"
          }
        },
        {
          kind: "block",
          type: "controls_for",
          fields: {
            VAR: {
              id: "XECl4GVs-^+[?1V:UI%Z",
              name: "i"
            }
          },
          values: {
            FROM: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "1"
              }
            },
            TO: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "10"
              }
            },
            BY: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "1"
              }
            }
          }
        },
        {
          kind: "block",
          type: "controls_forEach",
          fields: {
            VAR: {
              id: "zbX-3A%GYRcl`Ngw=KCn",
              name: "j"
            }
          }
        },
        {
          kind: "block",
          type: "controls_flow_statements",
          fields: {
            FLOW: "BREAK"
          }
        }
      ]
    },
    {
      kind: "category",
      name: "Math",
      colour: "#5b67a5",
      contents: [
        {
          kind: "block",
          type: "math_number",
          fields: {
            NUM: "0"
          }
        },
        {
          kind: "block",
          type: "math_arithmetic",
          fields: {
            OP: "ADD"
          },
          values: {
            A: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "1"
              }
            },
            B: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "1"
              }
            }
          }
        },
        {
          kind: "block",
          type: "math_single",
          fields: {
            OP: "ROOT"
          },
          values: {
            NUM: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "9"
              }
            }
          }
        },
        {
          kind: "block",
          type: "math_trig",
          fields: {
            OP: "SIN"
          },
          values: {
            NUM: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "45"
              }
            }
          }
        },
        {
          kind: "block",
          type: "math_constant",
          fields: {
            CONSTANT: "PI"
          }
        },
        {
          kind: "block",
          type: "math_number_property",
          fields: {
            PROPERTY: "EVEN"
          },
          values: {
            NUMBER_TO_CHECK: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "0"
              }
            }
          }
        },
        {
          kind: "block",
          type: "math_round",
          fields: {
            OP: "ROUND"
          },
          values: {
            NUM: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "3.1"
              }
            }
          }
        },
        {
          kind: "block",
          type: "math_on_list",
          mutation: {
            op: "SUM"
          },
          fields: {
            OP: "SUM"
          }
        },
        {
          kind: "block",
          type: "math_modulo",
          values: {
            DIVIDEND: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "64"
              }
            },
            DIVISOR: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "10"
              }
            }
          }
        },
        {
          kind: "block",
          type: "math_constrain",
          values: {
            VALUE: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "50"
              }
            },
            LOW: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "1"
              }
            },
            HIGH: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "100"
              }
            }
          }
        },
        {
          kind: "block",
          type: "math_random_int",
          values: {
            FROM: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "1"
              }
            },
            TO: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "100"
              }
            }
          }
        },
        {
          kind: "block",
          type: "math_random_float"
        }
      ]
    },
    {
      kind: "category",
      name: "Text",
      colour: "#5ba58c",
      contents: [
        {
          kind: "block",
          type: "text",
          fields: {
            TEXT: ""
          }
        },
        {
          kind: "block",
          type: "text_join",
          mutation: {
            items: "2"
          }
        },
        {
          kind: "block",
          type: "text_append",
          fields: {
            VAR: {
              id: "7j+{(eU@3Nf`G7G@/J%}",
              name: "item"
            }
          },
          values: {
            TEXT: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: ""
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_length",
          values: {
            VALUE: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: "abc"
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_isEmpty",
          values: {
            VALUE: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: ""
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_indexOf",
          fields: {
            END: "FIRST"
          },
          values: {
            VALUE: {
              kind: "block",
              type: "variables_get",
              fields: {
                VAR: {
                  id: ",Yd_J]Tvz2@pj)r9I|p$",
                  name: "text"
                }
              }
            },
            FIND: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: "abc"
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_charAt",
          mutation: {
            at: "true"
          },
          fields: {
            WHERE: "FROM_START"
          },
          values: {
            VALUE: {
              kind: "block",
              type: "variables_get",
              fields: {
                VAR: {
                  id: ",Yd_J]Tvz2@pj)r9I|p$",
                  name: "text"
                }
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_getSubstring",
          mutation: {
            at1: "true",
            at2: "true"
          },
          fields: {
            WHERE1: "FROM_START",
            WHERE2: "FROM_START"
          },
          values: {
            STRING: {
              kind: "block",
              type: "variables_get",
              fields: {
                VAR: {
                  id: ",Yd_J]Tvz2@pj)r9I|p$",
                  name: "text"
                }
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_changeCase",
          fields: {
            CASE: "UPPERCASE"
          },
          values: {
            TEXT: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: "abc"
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_trim",
          fields: {
            MODE: "BOTH"
          },
          values: {
            TEXT: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: "abc"
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_print",
          values: {
            TEXT: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: "abc"
              }
            }
          }
        },
        {
          kind: "block",
          type: "text_prompt_ext",
          mutation: {
            type: "TEXT"
          },
          fields: {
            TYPE: "TEXT"
          },
          values: {
            TEXT: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: "abc"
              }
            }
          }
        }
      ]
    },
    {
      kind: "category",
      name: "Lists",
      colour: "#745ba5",
      contents: [
        {
          kind: "block",
          type: "lists_create_with",
          mutation: {
            items: "0"
          }
        },
        {
          kind: "block",
          type: "lists_create_with",
          mutation: {
            items: "3"
          }
        },
        {
          kind: "block",
          type: "lists_repeat",
          values: {
            NUM: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "5"
              }
            }
          }
        },
        {
          kind: "block",
          type: "lists_length"
        },
        {
          kind: "block",
          type: "lists_isEmpty"
        },
        {
          kind: "block",
          type: "lists_indexOf",
          fields: {
            END: "FIRST"
          },
          values: {
            VALUE: {
              kind: "block",
              type: "variables_get",
              fields: {
                VAR: {
                  id: "/KVKU4..*N9aqt7tc`r)",
                  name: "list"
                }
              }
            }
          }
        },
        {
          kind: "block",
          type: "lists_getIndex",
          mutation: {
            statement: "false"
          },
          fields: {
            MODE: "GET",
            WHERE: "FROM_START"
          },
          values: {
            VALUE: {
              kind: "block",
              type: "variables_get",
              fields: {
                VAR: {
                  id: "/KVKU4..*N9aqt7tc`r)",
                  name: "list"
                }
              }
            }
          }
        },
        {
          kind: "block",
          type: "lists_setIndex",
          mutation: {
            at: "true"
          },
          fields: {
            MODE: "SET",
            WHERE: "FROM_START"
          },
          values: {
            LIST: {
              kind: "block",
              type: "variables_get",
              fields: {
                VAR: {
                  id: "/KVKU4..*N9aqt7tc`r)",
                  name: "list"
                }
              }
            }
          }
        },
        {
          kind: "block",
          type: "lists_getSublist",
          mutation: {
            at1: "true",
            at2: "true"
          },
          fields: {
            WHERE1: "FROM_START",
            WHERE2: "FROM_START"
          },
          values: {
            LIST: {
              kind: "block",
              type: "variables_get",
              fields: {
                VAR: {
                  id: "/KVKU4..*N9aqt7tc`r)",
                  name: "list"
                }
              }
            }
          }
        },
        {
          kind: "block",
          type: "lists_split",
          mutation: {
            mode: "SPLIT"
          },
          fields: {
            MODE: "SPLIT"
          },
          values: {
            DELIM: {
              kind: "block",
              type: "text",
              fields: {
                TEXT: ","
              }
            }
          }
        },
        {
          kind: "block",
          type: "lists_sort",
          fields: {
            TYPE: "NUMERIC",
            DIRECTION: "1"
          }
        }
      ]
    },
    {
      kind: "category",
      name: "Colour",
      colour: "#a5745b",
      contents: [
        {
          kind: "block",
          type: "colour_picker",
          fields: {
            COLOUR: "#ff0000"
          }
        },
        {
          kind: "block",
          type: "colour_random"
        },
        {
          kind: "block",
          type: "colour_rgb",
          values: {
            RED: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "100"
              }
            },
            GREEN: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "50"
              }
            },
            BLUE: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "0"
              }
            }
          }
        },
        {
          kind: "block",
          type: "colour_blend",
          values: {
            COLOUR1: {
              kind: "block",
              type: "colour_picker",
              fields: {
                COLOUR: "#ff0000"
              }
            },
            COLOUR2: {
              kind: "block",
              type: "colour_picker",
              fields: {
                COLOUR: "#3333ff"
              }
            },
            RATIO: {
              kind: "block",
              type: "math_number",
              fields: {
                NUM: "0.5"
              }
            }
          }
        }
      ]
    },
    {
      kind: "sep"
    },
    {
      kind: "category",
      name: "Variables",
      colour: "#a55b80",
      custom: "VARIABLE"
    },
    {
      kind: "category",
      name: "Functions",
      colour: "#995ba5",
      custom: "PROCEDURE"
    },
    {
      kind: "sep"
    },
    {
      kind: "category",
      name: "Kuika Parameters",
      colour: "#5CA699",
      contents: [
        { kind: "block", type: "kparameter_string" },
        { kind: "block", type: "kparameter_int16" },
        { kind: "block", type: "kparameter_int32" },
        { kind: "block", type: "kparameter_int64" },
        { kind: "block", type: "kparameter_decimal" },
        { kind: "block", type: "kparameter_double" },
        { kind: "block", type: "kparameter_boolean" },
        { kind: "block", type: "kparameter_datetime" }
      ]
    }
  ]
};

const functionCallerBlockTypes = ["procedures_callreturn", "procedures_callnoreturn"];

interface ICreateBlocklyActionState {
  xml: string;
  isNameOnEdit: boolean;
  actionName: string;
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}
interface ICreateBlocklyActionProps {
  actionDatasource: IDatasourceDto;
  actionToEdit?: IActionDefinitionDto;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

class CreateBlocklyAction extends PureSdComponent<ICreateBlocklyActionProps & IWithSDProps, ICreateBlocklyActionState> {
  formRef = createRef<FormInstance>();

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateBlocklyActionProps & IWithSDProps) {
    super(props);
    if (props.actionToEdit?.blocklyActionInfo) {
      const editingBlocklyActionInfo = JSON.parse(
        (props.actionToEdit?.blocklyActionInfo as unknown as string) || ""
      ) as IBlocklyActionInfo;
      this.state = {
        xml: editingBlocklyActionInfo.xml,
        isNameOnEdit: false,
        actionName: props.actionToEdit.name,
        saveStatus: "idle"
      };
      return;
    }

    this.state = {
      xml: initialXml,
      isNameOnEdit: false,
      actionName: "",
      saveStatus: "idle"
    };
  }

  componentDidMount() {
    this.handleLoadBlocklyAction();
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate(prevProps: ICreateBlocklyActionProps & IWithSDProps) {
    if (
      this.props.actionDatasource?.id !== prevProps.actionDatasource?.id ||
      this.props.actionToEdit !== prevProps.actionToEdit
    ) {
      this.handleLoadBlocklyAction();
    }
    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
    }
  }

  handleLoadBlocklyAction = () => {
    const {
      actionToEdit,
      sd: { activeCreatingActionName }
    } = this.props;

    const state: ICreateBlocklyActionState = { ..._.cloneDeep(this.state) };
    state.xml = initialXml;
    // A new action is called what its creator named it — the tree, the switcher's "+" and the
    // create flow each generate a unique name — and only a route that names nothing falls back.
    state.actionName =
      actionToEdit?.name ||
      activeCreatingActionName ||
      KMainFunctions.defaultUniqueListElementNameGenerator(
        this.props.sd.actionDefinitions ?? [],
        "name",
        "Blockly_Action"
      );

    if (actionToEdit?.blocklyActionInfo) {
      const editingBlocklyActionInfo = JSON.parse(
        (actionToEdit?.blocklyActionInfo as unknown as string) || ""
      ) as IBlocklyActionInfo;
      state.xml = editingBlocklyActionInfo.xml;
    }

    this.setState(state, () => {
      // The field starts from the name the chip shows, so a rename edits that name.
      this.formRef.current?.setFieldsValue({ name: state.actionName });
      this.objectHash = this.getObjectHash();
    });
  };

  getObjectHash(): string {
    const { actionName, xml } = { ...this.state };

    return ObjectHash({ actionName, xml });
  }

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  handleXmlChange = (xml) => {
    this.setState({ xml });
  };

  generateParameters = (xml: string) => {
    const parameters: IActionParameter[] = [];

    const parser = new DOMParser();
    const parsedXML = parser.parseFromString(xml, "text/xml");
    const stringParamNodes = parsedXML.querySelectorAll("block[type='kparameter_string']");
    const booleanParamNodes = parsedXML.querySelectorAll("block[type='kparameter_boolean']");
    const int16ParamNodes = parsedXML.querySelectorAll("block[type='kparameter_int16']");
    const int32ParamNodes = parsedXML.querySelectorAll("block[type='kparameter_int32']");
    const int64ParamNodes = parsedXML.querySelectorAll("block[type='kparameter_int64']");
    const decimalParamNodes = parsedXML.querySelectorAll("block[type='kparameter_decimal']");
    const doubleParamNodes = parsedXML.querySelectorAll("block[type='kparameter_double']");
    const datetimeParamNodes = parsedXML.querySelectorAll("block[type='kparameter_datetime']");

    const pushParameter = (name: string, Typename: BackEndValueType | "DateTime") => {
      parameters.push({
        name,
        Typename
      } as IActionParameter);
    };

    stringParamNodes.forEach((node) => {
      pushParameter(node.firstChild?.textContent ?? "", "String");
    });
    booleanParamNodes.forEach((node) => {
      pushParameter(node.firstChild?.textContent ?? "", "Boolean");
    });
    int16ParamNodes.forEach((node) => {
      pushParameter(node.firstChild?.textContent ?? "", "Int16");
    });
    int32ParamNodes.forEach((node) => {
      pushParameter(node.firstChild?.textContent ?? "", "Int32");
    });
    int64ParamNodes.forEach((node) => {
      pushParameter(node.firstChild?.textContent ?? "", "Int64");
    });
    decimalParamNodes.forEach((node) => {
      pushParameter(node.firstChild?.textContent ?? "", "Decimal");
    });
    doubleParamNodes.forEach((node) => {
      pushParameter(node.firstChild?.textContent ?? "", "Double");
    });
    datetimeParamNodes.forEach((node) => {
      pushParameter(node.firstChild?.textContent ?? "", "DateTime");
    });

    return parameters.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  };

  saveAction = async (): Promise<void> => {
    // The submit button is live again as soon as the ✓ replaces the spinner, so a second Enter
    // would re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return;
    try {
      this.saveStatus.start();

      const { xml, actionName } = _.cloneDeep(this.state);
      const code = javascriptGenerator.workspaceToCode();

      const action = {
        ...(this.props.actionToEdit || {}),
        name: actionName,
        kDatasource_Id: this.props.actionDatasource.id,
        actionType: EDatasourceType.Blockly,
        blocklyActionInfo: JSON.stringify({
          code,
          xml
        }) as unknown as IBlocklyActionInfo,
        parameters: this.generateParameters(xml)
      } as IActionDefinitionDto;

      const response = await DatasourceService.SaveAction(action);

      if (response.status !== 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return;
      }
      this.props.sd.refreshDatabases();
      this.props.sd.refreshActions();
      // The ✓ goes up first and the editor leaves after it — see `SaveStatus`.
      // A close confirmation this save answered ("Update & close") steps aside at once, so
      // the ✓ on the button is what is seen before the editor leaves.
      this.props.setShowModal(false);
      this.saveStatus.succeed((): void => {
        // The list behind the editor lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: actionName, parentId: this.props.actionDatasource?.id });
        if (this.props.actionDatasource) this.closeDataSource();
        else {
          const ds = response.data.find((ds) => ds.name === actionName);
          this.props.sd.setActiveEditingDatasource(ds);
        }
      });
    } catch (e) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(e);
    }
  };

  existingNameValidator = async () => {};

  handleSubmitNameChange = () => {
    const errors = this.formRef.current?.getFieldError("name");
    if (errors && errors.length > 0) {
      return;
    }
    this.setState({ isNameOnEdit: false, actionName: this.formRef.current?.getFieldValue("name") });
  };

  handleAbortNameChange = () => {
    if (!this.state.actionName) {
      KMainFunctions.displayWarningNotification("Please enter a valid action name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.actionName
    });
    this.setState({ isNameOnEdit: false });
  };

  handleNameInputOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.handleSubmitNameChange();
  };

  handleWorkspaceOnChange = (workspace: Blockly.WorkspaceSvg) => {
    if (workspace.isDragging() || workspace.getToolbox()?.getSelectedItem()) return;
    const blocks = workspace.getAllBlocks();

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      if (block.getParent()) continue;
      if (functionCallerBlockTypes.includes(block.type)) {
        workspace.undo(false);
        break;
      }
    }
  };

  /** The action renames in place, so the bar hosts the field as well as the name. */
  renderHeaderTitle = (): ReactNode => (
    <KuikaEditorTitle
      entityLabel="action"
      isRenaming={this.state.isNameOnEdit}
      // An as-yet-unnamed action has nothing to discard back to.
      canAbortRename={!!this.state.actionName}
      onStartRename={() => this.setState({ isNameOnEdit: true })}
      onConfirmRename={this.handleSubmitNameChange}
      onAbortRename={this.handleAbortNameChange}
      editTestId="blocklyActionNameEditButton"
      confirmTestId="blocklyActionNameConfirmButton"
      abortTestId="blocklyActionNameCancelButton"
      identity={
        <BlocklyActionSwitcherChip
          sd={this.props.sd}
          setNextCallbackFunction={this.props.setNextCallbackFunction}
          datasource={this.props.actionDatasource}
          currentActionId={this.props.actionToEdit?.id}
          currentName={this.state.actionName}
          onManageAll={() => this.handleCloseDatasource()}
        />
      }
      renameField={
        <Form.Item
          name="name"
          rules={[
            { required: true, message: "Please enter an action name" },
            // Action names become generated method names; the old rule allowed spaces and refused
            // the underscore every generated Blockly name carries.
            { pattern: ACTION_NAME_PATTERN, message: ACTION_NAME_MESSAGE },
            { validator: this.existingNameValidator }
          ]}
        >
          <KuikaInput
            onKeyDown={this.handleNameInputOnEnter}
            placeholder="Action name"
            data-testid="blocklyActionNameInput"
          />
        </Form.Item>
      }
    />
  );

  render(): ReactNode {
    // The dimming overlay and the close-confirmation dialog ask the same question the button's
    // status answers; they lift while the ✓ is still up, which is when the editor is leaving anyway.
    const isSaving: boolean = this.state.saveStatus === "loading";

    return (
      <Form name="dsModal" ref={this.formRef} onFinish={this.saveAction} layout="vertical" style={{ height: "100%" }}>
        <KuikaSpinnerSmall spinning={isSaving}>
          <div className="kuika__data-diagram__create-con kuika__data-diagram__create-con--bar">
            <div className="kuika-blockly-action">
              <KuikaEditorHeader
                title={this.state.actionName}
                backLabel="Back to Datasources"
                renderTitle={this.renderHeaderTitle}
                submitLabel={this.props.actionDatasource && this.props.actionToEdit ? "UPDATE" : "CREATE"}
                submitHtmlType="submit"
                status={this.state.saveStatus}
                onCancel={() => this.handleCloseDatasource()}
                cancelTestId="blocklyActionBackButton"
                submitTestId="blocklyActionSubmitButton"
              />
              {/* Takes what the bar leaves instead of subtracting a number: the workspace
                  measures itself against this box, and the old 64.4px was the height of a
                  header that no longer exists. */}
              <div className="kuika__data-diagram__create-con__container kuika-blockly-action__body">
                <BlocklyWorkspace
                  className="kuika-blockly-workspace"
                  initialXml={this.state.xml}
                  toolboxConfiguration={toolbox}
                  workspaceConfiguration={{
                    grid: {
                      spacing: 20,
                      length: 3,
                      colour: "#ccc",
                      snap: true
                    }
                  }}
                  onWorkspaceChange={this.handleWorkspaceOnChange}
                  onXmlChange={this.handleXmlChange}
                />
              </div>
            </div>
          </div>
        </KuikaSpinnerSmall>
        <CloseConfirmationModal
          itemName={this.state.actionName}
          closeType={"Blockly Action"}
          triggerModal={this.props.showModal}
          isLoading={isSaving}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </Form>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createBlocklyAction = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreateBlocklyActionProps>(CreateBlocklyAction));
export { createBlocklyAction as CreateBlocklyAction };
